import {
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  input,
  OnChanges,
  OnInit,
  output,
  SimpleChanges,
} from '@angular/core';
import { ButtonState, IndexedPhoneButton, PhoneButton, PhoneButtonTypes } from '../../models/phone-button';

import { get } from 'lodash';
import { ZiroIcons } from '../../models/ziro-icons.enum';
import { DragDropMode } from '../drag-drop-mode.enum';
import { LineButtonPeripheralSummary } from '../layout/phone-buttons-standard.component';
import { PhoneButtonsService } from '../shared/phone-buttons.service';
import { TelephoneNumberFilter } from '../../filters/telephone-number.filter';
import { LineButton } from '../../models/generated/ziroModels';
import { NgClass, NgStyle } from '@angular/common';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'ziro-phone-buttons-dnd-button',
  templateUrl: './phone-buttons-drag-n-drop-button.component.html',
  styleUrls: ['./phone-buttons-drag-n-drop-button.component.scss'],
  imports: [NgClass, NgbTooltip, TranslateModule, NgStyle],
})
export class PhoneButtonsDragNDropButtonComponent implements OnInit, OnChanges {
  private phoneButtonsService = inject(PhoneButtonsService);
  private telephoneNumberFilter = inject(TelephoneNumberFilter);
  private changeDetectorRef = inject(ChangeDetectorRef);

  readonly isPending = input<boolean>();
  // TODO: Skipped for migration because:
  //  Your application code writes to the input. This prevents migration.
  @Input() button: PhoneButton;
  readonly buttonIdx = input<number>();
  readonly userMode = input<DragDropMode>();
  readonly buttonState = input<ButtonState>(new ButtonState());
  readonly inverse = input(false);
  readonly dnSummary = input<LineButtonPeripheralSummary>();
  readonly deletedPhoneButton = output<IndexedPhoneButton>();

  ziroIcons = ZiroIcons;
  DragDropMode = DragDropMode;
  tooltipMessage: string;
  clearTooltipMessage: string;
  showVoicemailIcon = false;
  showUccxIcon = false;

  ngOnInit(): void {
    const buttonIdx = this.buttonIdx();
    const buttonState = this.buttonState();
    if (buttonIdx === 0 && !this.isHelpdeskMode()) {
      this.tooltipMessage = 'tkey;shared.device.phone.phone_buttons.button_tab.tooltip.line_one';
    } else if (buttonState.uneditable && this.userMode() === DragDropMode.SELF_SERVE) {
      this.tooltipMessage = 'tkey;shared.device.phone.phone_buttons.button_tab.tooltip.uneditable';
    } else if (buttonState.unmovable) {
      this.tooltipMessage = 'tkey;shared.device.phone.phone_buttons.button_tab.tooltip.unmovable';
    } else if (buttonIdx === 0) {
      this.clearTooltipMessage =
        'tkey;shared.device.phone.phone_buttons.button_tab.tooltip.helpdesk_mode.line_one.clear.button';
    } else {
      this.tooltipMessage = null;
    }
    this.isEmpty(this.button);
    this.phoneButtonsService.lineButtonSummary.subscribe((lineButtonSummary) => {
      if (this.button.type === PhoneButtonTypes.line) {
        const lineDn = (this.button as LineButton).dn;
        if (!!lineDn && !!lineButtonSummary && lineDn.id === lineButtonSummary.id) {
          this.showUccxIcon = lineButtonSummary.isUccxAgent;
          this.changeDetectorRef.detectChanges();
        }
      }
    });
    this.showUccxIcon = this.isUccxAgent(this.button);

    this.phoneButtonsService.updateTpRef.subscribe((tpRef) => {
      const dnSummary = this.dnSummary();
      if (this.button.type === PhoneButtonTypes.line && dnSummary) {
        const lineDn = (this.button as LineButton).dn;
        if (!!lineDn && lineDn.extension === tpRef.destination) {
          dnSummary.didTranslationPattern = tpRef;
        }
      }
    });
  }

  isButtonLabelLong(button: PhoneButton): boolean {
    return this.getButtonTypeLabel(button).length > 2;
  }

  isEmpty(button: PhoneButton): boolean {
    const phoneButtonSummary = this.phoneButtonsService.mapButtonToButtonSummary(button);
    return !phoneButtonSummary.value && !phoneButtonSummary.label;
  }

  getNonLineButtonValue(button: PhoneButton): string {
    if (this.phoneButtonsService.isSpeedDialButton(button)) {
      return button.value;
    } else if (this.phoneButtonsService.isBlfButton(button)) {
      return button.destination;
    } else if (this.phoneButtonsService.isServiceUrlButton(button)) {
      return button.subscriptionName;
    } else if (this.phoneButtonsService.isIntercomButton(button)) {
      return button.intercom?.extension;
    }
  }

  getButtonLabel(button: PhoneButton): string {
    return get(button, 'label') || get(button, 'lineFeature.label');
  }

  getButtonValue(button: PhoneButton): string {
    return get(button, 'value');
  }

  getButtonDestination(button: PhoneButton): string {
    return get(button, 'destination');
  }

  getButtonExtension(button: PhoneButton): string {
    return this.telephoneNumberFilter.transform(get(button, 'dn.extension'));
  }

  getButtonType(button: PhoneButton): string {
    return get(button, 'type');
  }

  getButtonTypeLabel(button: PhoneButton): string {
    return button.type
      .split(' ')
      .map((item) => item.toUpperCase().substring(0, 1))
      .join('');
  }

  openDeleteConfirmation(): void {
    if (!this.clearTooltipMessage) {
      this.buttonState().deleteToggled = true;
    }
  }

  closeDeleteConfirmation(): void {
    this.buttonState().deleteToggled = false;
  }

  deleteButton(): void {
    this.button = this.phoneButtonsService.newButtonOfType(this.button.type);
    this.deletedPhoneButton.emit({ index: this.buttonIdx(), button: this.button });
    this.showVoicemailIcon = false;
    this.showUccxIcon = false;
  }

  isSelfServeLine(button: PhoneButton): boolean {
    return this.phoneButtonsService.isLineButton(button) && this.userMode() === DragDropMode.SELF_SERVE;
  }

  isHelpdeskMode(): boolean {
    return this.userMode() === DragDropMode.HELPDESK;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.dnSummary?.currentValue?.voicemail === null) {
      this.showVoicemailIcon = false;
    }
  }

  isVoicemailExists(button: PhoneButton): boolean {
    if (this.phoneButtonsService.isLineButton(button) && this.isHelpdeskMode()) {
      return !!button.dn && !!this.dnSummary()?.voicemail;
    }
    return false;
  }

  translationPattern(button: PhoneButton): string {
    const dnSummary = this.dnSummary();
    if (this.phoneButtonsService.isLineButton(button) && button.dn && this.isHelpdeskMode() && dnSummary) {
      return this.telephoneNumberFilter.transform(dnSummary.didTranslationPattern?.pattern);
    }
  }

  displayButtonLabel(button: PhoneButton): string {
    if (Object.values(PhoneButtonTypes).some((val) => val === button.type)) {
      return !this.isEmpty(button) ? this.getButtonLabel(button) : this.getButtonType(button);
    }
    return this.getButtonType(button);
  }

  isPrimaryExtension(button: PhoneButton): boolean {
    if (this.phoneButtonsService.isLineButton(button) && this.isHelpdeskMode()) {
      return !!button.dn && !!this.dnSummary()?.isPrimaryExtension;
    }
    return false;
  }

  isUccxAgent(button: PhoneButton): boolean {
    if (this.phoneButtonsService.isLineButton(button) && this.isHelpdeskMode()) {
      return !!button.dn && !!this.dnSummary()?.isUccxAgent;
    }
    return false;
  }
}
